// react
import { useCallback, useMemo } from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// api
import {
  type PortfolioAnalysisOutput,
  useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import {
  useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery,
  useGetMatrikelMetadataMatrikelMetadataAccountIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMatrikel';
import { useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import {
  type PortfolioAnalysisSubscriptionOutput,
  useGetSubscriptionSubscriptionAccountIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';

// components
import usePortfolioDegreeDayCorrection from '@/features/energyxplorer/portfolio/hooks/usePortfolioDegreeDayCorrection';
import type {
  AnalysisType,
  MatrikelItem,
  MatrikelMetadata,
  MeterListEntry,
  PortfolioMeter,
} from '@/features/energyxplorer/types';

interface MatrikelAnalysisEntry extends Record<string, unknown> {
  matrikel_key: string;
}

interface MatrikelAnalysisResponse {
  MatrikelAnalysis?: MatrikelAnalysisEntry[];
}

const DEFAULT_OBJECT: MatrikelItem = {
  power: { meter_list: [], matrikel_analysis: {} },
  water: { meter_list: [], matrikel_analysis: {} },
  heat: { meter_list: [], matrikel_analysis: {} },
  metadata: { MatrikelKey: '' },
};

const useMatrikelList = () => {
  const { accountId, analysisType } = useParams({ strict: false });
  const { isDegreeDayCorrectionEnabled } = usePortfolioDegreeDayCorrection(
    analysisType as AnalysisType | undefined,
  );
  const {
    data: subscriptionData,
    isFetching: isSubscriptionFetching,
    isLoading: isSubscriptionLoading,
  } = useGetSubscriptionSubscriptionAccountIdGetQuery(
    { accountId },
    { skip: !accountId },
  ) as {
    data: PortfolioAnalysisSubscriptionOutput | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: powerData,
    isFetching: isPowerFetching,
    isLoading: isPowerLoading,
  } = useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
    { accountId, analysisType: 'power' },
    { skip: !accountId || !subscriptionData?.power },
  ) as {
    data: PortfolioAnalysisOutput | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: waterData,
    isFetching: isWaterFetching,
    isLoading: isWaterLoading,
  } = useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
    { accountId, analysisType: 'water' },
    { skip: !accountId || !subscriptionData?.water },
  ) as {
    data: PortfolioAnalysisOutput | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: heatData,
    isFetching: isHeatFetching,
    isLoading: isHeatLoading,
  } = useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
    {
      accountId,
      analysisType: 'heat',
      degreeDayCorrection: isDegreeDayCorrectionEnabled || undefined,
    },
    { skip: !accountId || !subscriptionData?.heat },
  ) as {
    data: PortfolioAnalysisOutput | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: powerMeterData,
    isFetching: isPowerMeterFetching,
    isLoading: isPowerMeterLoading,
  } = useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
    { analysisType: 'power', accountId },
    {
      skip: !accountId,
      pollingInterval: 0,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  ) as {
    data: PortfolioMeter[] | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: waterMeterData,
    isFetching: isWaterMeterFetching,
    isLoading: isWaterMeterLoading,
  } = useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
    { analysisType: 'water', accountId },
    { skip: !accountId },
  ) as {
    data: PortfolioMeter[] | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: heatMeterData,
    isFetching: isHeatMeterFetching,
    isLoading: isHeatMeterLoading,
  } = useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
    { analysisType: 'heat', accountId },
    { skip: !accountId },
  ) as {
    data: PortfolioMeter[] | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: matrikel_metadata,
    isFetching: isMatrikelMetadataFetching,
    isLoading: isMatrikelMetadataLoading,
  } = useGetMatrikelMetadataMatrikelMetadataAccountIdGetQuery(
    { accountId },
    { skip: !accountId },
  ) as {
    data: MatrikelMetadata[] | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const {
    data: matrikel_analysis,
    isFetching: isMatrikelAnalysisFetching,
    isLoading: isMatrikelAnalysisLoading,
  } = useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery(
    {
      accountId,
      degreeDayCorrection: isDegreeDayCorrectionEnabled || undefined,
    },
    { skip: !accountId },
  ) as {
    data: MatrikelAnalysisResponse | undefined;
    isFetching: boolean;
    isLoading: boolean;
  };

  const isLoading = useMemo(
    () =>
      isMatrikelMetadataLoading ||
      isMatrikelAnalysisLoading ||
      isPowerLoading ||
      isWaterLoading ||
      isHeatLoading ||
      isPowerMeterLoading ||
      isWaterMeterLoading ||
      isHeatMeterLoading ||
      isSubscriptionLoading,
    [
      isMatrikelMetadataLoading,
      isMatrikelAnalysisLoading,
      isPowerLoading,
      isWaterLoading,
      isHeatLoading,
      isPowerMeterLoading,
      isWaterMeterLoading,
      isHeatMeterLoading,
      isSubscriptionLoading,
    ],
  );

  const isFetching = useMemo(
    () =>
      isMatrikelMetadataFetching ||
      isMatrikelAnalysisFetching ||
      isPowerFetching ||
      isWaterFetching ||
      isHeatFetching ||
      isPowerMeterFetching ||
      isWaterMeterFetching ||
      isHeatMeterFetching ||
      isSubscriptionFetching,
    [
      isMatrikelMetadataFetching,
      isMatrikelAnalysisFetching,
      isPowerFetching,
      isWaterFetching,
      isHeatFetching,
      isPowerMeterFetching,
      isWaterMeterFetching,
      isHeatMeterFetching,
      isSubscriptionFetching,
    ],
  );

  const meterData = useCallback(
    (analysisType: AnalysisType): PortfolioMeter[] | undefined => {
      if (analysisType === 'power') return powerMeterData;
      if (analysisType === 'water') return waterMeterData;
      if (analysisType === 'heat') return heatMeterData;
      return undefined;
    },
    [powerMeterData, waterMeterData, heatMeterData],
  );

  const analysisData = useCallback(
    (analysisType: AnalysisType): PortfolioAnalysisOutput | undefined => {
      if (analysisType === 'power') return powerData;
      if (analysisType === 'water') return waterData;
      if (analysisType === 'heat') return heatData;
      return undefined;
    },
    [powerData, waterData, heatData],
  );

  const matrikel_list = useMemo((): MatrikelItem[] => {
    let result: MatrikelItem[] = [];

    if (matrikel_metadata) {
      result = matrikel_metadata.map((meta) => ({
        ...DEFAULT_OBJECT,
        metadata: { ...meta },
      }));
    }

    if (matrikel_analysis?.MatrikelAnalysis) {
      (['water', 'power', 'heat'] as AnalysisType[]).forEach((energy_type) => {
        if (subscriptionData?.[energy_type]) {
          result = result.map((item) => {
            let address = '';
            let meter_list: MeterListEntry[] = [];

            const meters = meterData(energy_type);
            const analysis = analysisData(energy_type);

            if (meters && analysis) {
              meter_list = meters
                .filter(
                  (m) =>
                    `${m.EjerlavKode}_${m.MatrikelNr}` ===
                      item.metadata.MatrikelKey ||
                    m.MatrikelKey === item.metadata.MatrikelKey,
                )
                .map((m) => {
                  const analysisMatch =
                    analysis.portfolio_analysis_list?.find((a) => {
                      if (energy_type === 'power') {
                        return a.metering_point_id === m.MeteringPointId;
                      }
                      let unit_id: unknown = null;
                      try {
                        unit_id = (a as { data_stream?: { unit_id?: unknown } })
                          .data_stream?.unit_id;
                      } catch {
                        unit_id = null;
                      }
                      return unit_id === m.UnitId;
                    }) ?? null;

                  address = analysisMatch?.main_address ?? address;
                  return {
                    analysis: analysisMatch ?? {},
                    metadata: m,
                    hasAnalysis: Boolean(analysisMatch),
                  } satisfies MeterListEntry;
                })
                .filter(
                  (entry) =>
                    entry.hasAnalysis ||
                    entry.metadata.UnitId ||
                    !(entry.metadata as Record<string, unknown>).meter_in_sync,
                );
            }

            return {
              ...item,
              [energy_type]: {
                ...item[energy_type as AnalysisType],
                meter_list,
                matrikel_analysis:
                  matrikel_analysis.MatrikelAnalysis!.find(
                    (m) => m.matrikel_key === item.metadata.MatrikelKey,
                  )?.[`matrikel_${energy_type}`] ?? {},
              },
              metadata: {
                ...item.metadata,
                Address: address || item.metadata.Address,
              },
            };
          });
        }
      });
    }

    return result;
  }, [
    analysisData,
    matrikel_analysis,
    matrikel_metadata,
    meterData,
    subscriptionData,
  ]);

  return {
    matrikel_list,
    isLoading,
    isFetching,
    powerSummedData: powerData?.summed_data,
    heatSummedData: heatData?.summed_data,
    waterSummedData: waterData?.summed_data,
  };
};

export default useMatrikelList;
